import { useState, useEffect } from "react";
import {
  Button,
  TwModal as Modal,
  Text,
  Icon,
} from "@virkesborsen/ui-component-library";
import { TailwindWrapper } from "../../../TailwindWrapper/TailwindWrapper.jsx";
import { useDrone } from "../../context/DroneContext.js";
import { clearOrderFromCookie, trackDroneCheckoutEvent } from "./utils.js";
import { formatPriceWithCurrency } from "../../utils.js";
import { ConfirmClosePopup } from "./components/ConfirmClosePopup.js";
import * as Steps from "./steps/index";
import { useModalCloseOnEsc } from "../../utils.js";

export default function DroneCheckout({}) {
  const {
    currentStep,
    isCheckoutOpen,
    nextStep,
    previousStep,
    selectedProperties,
    orderPrices,
    extraData,
    setErrorMessages,
    resetCheckout,
  } = useDrone();

  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);

  const shouldHideNavigation = currentStep > 2;
  const shouldHideFooter = currentStep === 3;
  const hidePriceSummary = currentStep === 4 || !orderPrices.discountPrice;

  useEffect(() => {
    if (currentStep > 1) {
      setIsNextButtonDisabled(false);
    }
  }, [currentStep]);

  const handleClose = () => {
    setIsConfirmCloseOpen(false);
    clearOrderFromCookie();
    resetCheckout();
    trackDroneCheckoutEvent("cancelled", currentStep);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !selectedProperties.length) {
      setErrorMessages((prev) => ({
        ...prev,
        properties: "Du måste lägga till minst en fastighet för att forsätta",
      }));
      return;
    }

    // ObstacleInfo
    if (currentStep === 2) {
      const errors = {};

      if (extraData?.hasObstacles === undefined) {
        errors.obstacleInfo = "Du måste ange om det finns hinder";
      }
      if (extraData?.locationTerms !== true) {
        errors.locationTerms =
          "Du måste bekräfta att din fastighet inte ligger på en ö eller på militärt område";
      }
      if (extraData?.ownershipTerms !== true) {
        errors.ownershipTerms =
          "Du måste bekräfta att du äger fastigheten eller har tillåtelse från fastighetsägaren att beställa tjänsten";
      }

      if (Object.keys(errors).length > 0) {
        setErrorMessages((prev) => ({ ...prev, ...errors }));
        return;
      }
    }

    return nextStep();
  };

  const handleDisableNextButton = (isEnabled) => {
    setIsNextButtonDisabled(isEnabled);
  };

  useModalCloseOnEsc(isCheckoutOpen, handleClose)

  const getNavigationButtonLabel = () => {
    if (currentStep === 2) {
      return orderPrices.discountPrice > 20000
        ? "Slutför beställning"
        : "Gå vidare till Klarna";
    }

    if (currentStep === 4) {
      return "Stäng";
    }

    return "Nästa steg";
  };

  const getHeaderText = () => {
    if (currentStep === 1) {
      return {
        title: "Beställ drönarflygning",
        subtitle: "Utöver Ortofoto välj eventuell skadeanalys som tillägg",
      };
    }
    return {
      title: "Tillträde & hinder inför flygning",
      subtitle: "Ange eventuella hinder",
    };
  };

  if (currentStep === 3) {
    return (
      <>
        <Steps.Payment
          isOpen={isCheckoutOpen}
          handleClose={handleConfirmClose}
        />
        <TailwindWrapper>
          <ConfirmClosePopup
            isOpen={isConfirmCloseOpen}
            setIsOpen={setIsConfirmCloseOpen}
            handleClose={handleClose}
          />
        </TailwindWrapper>
      </>
    );
  }

  const headerText = getHeaderText();

  return (
    <TailwindWrapper>
      <Modal tw isOpen={isCheckoutOpen} borderless={currentStep === 1}>
        <Modal.Header tw className={shouldHideNavigation ? "hidden" : ""}>
          <div className="flex w-full justify-between gap-4 items-start">
            <div className="flex flex-row gap-4 items-center">
              <Button
                tw
                variant="tertiary"
                size="icon"
                onClick={previousStep}
                className={currentStep === 1 ? "opacity-0" : ""}
              >
                <Icon
                  tw
                  name="ArrowRightSimple"
                  size="large"
                  className="rotate-180"
                />
              </Button>
              <div className="flex flex-col gap-1">
                <Text variant="labelLargeMedium">{headerText.title}</Text>
                <Text variant="labelMediumRegular" className="text-cloud-900">
                  {headerText.subtitle}
                </Text>
              </div>
            </div>
            <Button
              tw
              variant="tertiary"
              size="icon"
              onClick={handleConfirmClose}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="large"
              />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content tw>
          {currentStep === 1 && (
            <Steps.Order disableNextButton={handleDisableNextButton} />
          )}
          {currentStep === 2 && <Steps.ObstacleInfo />}
          {/* 
            // Klarna HTML is rendered outside TailwindWrapper because Klarna Checkout cannot connect to its external API when inside a shadowRoot, which TailwindWrapper requires.
            which is used to isolate Tailwind styling here. 
          */}
          {currentStep === 4 && <Steps.ThankYou />}
        </Modal.Content>
        {!shouldHideFooter && (
          <Modal.Footer tw>
            <div
              className={[
                "flex flex-col justify-center gap-2",
                hidePriceSummary ? "opacity-0" : "",
              ].join(" ")}
            >
              <div className="flex flex-row gap-2 items-baseline">
                <Text variant="subheaderSmallMedium">
                  {formatPriceWithCurrency(orderPrices?.discountPrice)}
                </Text>
                {orderPrices.fullPrice && (
                  <Text
                    variant="labelMediumRegular"
                    className={`text-charcoal-200 line-through `}
                  >
                    {formatPriceWithCurrency(orderPrices.fullPrice)}
                  </Text>
                )}
                <Text
                  variant="labelMediumRegularDense"
                  className="text-charcoal-300"
                >
                  exkl. moms
                </Text>
              </div>
            </div>
            <Button
              tw
              variant="primary"
              onClick={handleNextStep}
              disabled={isNextButtonDisabled}
            >
              {getNavigationButtonLabel()}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
      <ConfirmClosePopup
        isOpen={isConfirmCloseOpen}
        setIsOpen={setIsConfirmCloseOpen}
        handleClose={handleClose}
      />
    </TailwindWrapper>
  );
}
